import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSimulatorConfiguration } from "./useSimulatorConfiguration";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { useFlagWithLoading } from "@/v2/components/feature-flag/FeatureFlagContext";

const configurationViews = [
  "general",
  "end_of_conversation",
  "escalations",
  "install",
  "appearance",
  "layout",
  "skills",
  "resources",
  "advanced",
  "post_answer_actions",
  "knowledge_hub",
] as const;
export type ConfigurationView = (typeof configurationViews)[number];

export const configurationViewToLabel: Record<ConfigurationView, string> = {
  general: "General",
  end_of_conversation: "End of Conversation",
  escalations: "Escalations",
  appearance: "Appearance",
  layout: "Layout",
  install: "Install",
  skills: "Skills",
  resources: "Resources",
  advanced: "Advanced",
  post_answer_actions: "Post-Answer Actions",
  knowledge_hub: "Knowledge Hub",
};

const supportedChannels: SimulatorChannel[] = [
  "autonomous_ticket_event",
  "autonomous_quackchat",
  "copilot",
  "knowledge_hub",
  "slackbot",
];

export function isChannelSupported(channel?: SimulatorChannel) {
  return channel != null && supportedChannels.includes(channel);
}

const channelConfigurationViews: Record<
  SimulatorChannel,
  ConfigurationView[] | null
> = {
  autonomous_ticket_event: [
    "general",
    "escalations",
    "post_answer_actions",
    "skills",
    "resources",
    "advanced",
    "install",
  ],
  autonomous_quackchat: [
    "general",
    "end_of_conversation",
    "escalations",
    "appearance",
    "layout",
    "skills",
    "resources",
    "install",
  ],
  helpcenter: null,
  slackbot: ["general", "skills", "resources"],
  copilot: ["general", "skills", "resources", "install"],
  knowledge_hub: ["knowledge_hub"],
};

export const automationConfigurationUris = {
  root: "/automation/:simulatorId/configurations",
  general: "general",
  end_of_conversation: "end-of-conversation",
  escalations: "escalations",
  install: "install",
  appearance: "appearance",
  layout: "layout",
  skills: "skills",
  resources: "resources",
  advanced: "advanced",
  post_answer_actions: "post-answer-actions",
  knowledge_hub: "knowledge-hub",
  getRootUri: (simulatorId: number) =>
    `/automation/${simulatorId}/configurations`,
};

export const configurationViewToUri: Record<ConfigurationView, string> = {
  general: automationConfigurationUris.general,
  end_of_conversation: automationConfigurationUris.end_of_conversation,
  escalations: automationConfigurationUris.escalations,
  appearance: automationConfigurationUris.appearance,
  layout: automationConfigurationUris.layout,
  install: automationConfigurationUris.install,
  skills: automationConfigurationUris.skills,
  resources: automationConfigurationUris.resources,
  advanced: automationConfigurationUris.advanced,
  post_answer_actions: automationConfigurationUris.post_answer_actions,
  knowledge_hub: automationConfigurationUris.knowledge_hub,
};

export function useGetChannelConfigurationViews(): {
  channel?: SimulatorChannel;
  views: ConfigurationView[];
  loadingViews: boolean;
} {
  const navigate = useNavigate();
  const { simulatorId } = useParams();
  const { simulator, loadingSimulator } =
    useSimulatorConfiguration(simulatorId);

  const { value: isPostAnswerActionsEnabled, loading: loadingPostAnswerActions } =
    useFlagWithLoading("postTicketAnswerActions", false);
  const { value: isLayoutConfigurationEnabled, loading: loadingLayout } =
    useFlagWithLoading("quackchatLayoutConfiguration", false);

  return useMemo(() => {
    const loadingViews = loadingSimulator || loadingPostAnswerActions || loadingLayout;

    if (loadingSimulator || simulator == null) {
      return { views: [], loadingViews };
    }

    if (simulator.channel == null || !isChannelSupported(simulator.channel)) {
      navigate(`/`);
      return { views: [], loadingViews };
    }

    let views = channelConfigurationViews[simulator.channel] ?? [];

    if (!loadingPostAnswerActions && !isPostAnswerActionsEnabled) {
      views = views.filter((v) => v !== "post_answer_actions");
    }

    if (!loadingLayout && !isLayoutConfigurationEnabled) {
      views = views.filter((v) => v !== "layout");
    }

    return { channel: simulator.channel, views, loadingViews };
  }, [
    navigate,
    loadingSimulator,
    simulator?.channel,
    isPostAnswerActionsEnabled,
    loadingPostAnswerActions,
    isLayoutConfigurationEnabled,
    loadingLayout,
  ]);
}
